import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import Text from "../../components/Text";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/MessageBox";
import { useTranslation } from "../../hooks/useTranslation";
import { useInventoryApi, type MoveRequest } from "../../services/inventoryApi";
import shared from "./storageShared.module.css";
import styles from "./StorageActivityTab.module.css";

const POLL_INTERVAL_MS = 4000;

interface Props {
  storageId: string;
  onCountChange: (count: number) => void;
}

export default function StorageActivityTab(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();

  const [moveRequests, setMoveRequests] = createStore<MoveRequest[]>([]);
  const [loading, setLoading] = createSignal(true);

  const isActive = (request: MoveRequest) => request.status === "Pending" || request.status === "InProgress";

  async function load(silent = false) {
    try {
      const requests = await inv.fetchMoveRequests(props.storageId);
      setMoveRequests(reconcile(requests, { key: "id" }));
      props.onCountChange(requests.length);
    } catch (e: any) {
      if (!silent) toast?.error(e?.message ?? t("STORAGE_ACTIVITY_LOAD_FAILED"));
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (!moveRequests.some(isActive)) return;
      void load(true);
    }, POLL_INTERVAL_MS);
    onCleanup(() => clearInterval(timer));
  });

  async function cancel(request: MoveRequest) {
    try {
      await inv.cancelMoveRequest(request.id);
      await load();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_MOVE_CANCEL_FAILED"));
    }
  }

  function statusClass(request: MoveRequest) {
    if (request.status === "Completed") return styles.stCompleted;
    if (request.status === "Failed") return styles.stFailed;
    if (request.status === "Cancelled") return styles.stCancelled;
    return styles.stPending;
  }

  return (
    <Show when={!loading()} fallback={<div class={shared.mutedNote}><i class="fa-solid fa-spinner fa-spin" /></div>}>
      <Show when={moveRequests.length > 0} fallback={
        <EmptyState illustration="jobs" title={t("STORAGE_NO_ACTIVITY")} />
      }>
        <For each={moveRequests}>
          {(request) => (
            <div class={styles.activityRow}>
              <div class={styles.activityHead}>
                <span class={styles.activityTitle}>{request.requestedQuantity}x {request.itemDisplayName}</span>
                <div class={styles.activityStatusWrap}>
                  <span class={`${styles.activityStatus} ${statusClass(request)}`}>
                    <Show when={isActive(request)}><i class="fa-solid fa-spinner fa-spin" /> </Show>
                    {t("STORAGE_MOVE_STATUS_" + request.status)}
                  </span>
                  <Show when={isActive(request)}>
                    <button
                      class={`${shared.iconBtn} ${shared.iconBtnDanger}`}
                      title={t("CANCEL")}
                      onClick={() => void cancel(request)}
                    >
                      <i class="fa-solid fa-xmark" />
                    </button>
                  </Show>
                </div>
              </div>
              <div class={styles.activitySub}>
                <i class="fa-solid fa-arrow-right" /> {request.destinationStorageName}
                <Show when={request.actualQuantityMoved > 0 && request.status !== "Pending"}>
                  {" "}<span>{t("STORAGE_MOVED", request.actualQuantityMoved)}</span>
                </Show>
                <Show when={request.failureReason}>{" "}<span>{request.failureReason}</span></Show>
              </div>
            </div>
          )}
        </For>
      </Show>
    </Show>
  );
}
